import { queryOptions, useQuery } from '@tanstack/react-query';
import type Big from 'big.js';

import { SECONDS_IN_DAY } from '@cast/constants';
import type { DateRange } from '@cast/shared-types';
import { WorkloadTrafficDestination } from '@cast/types';
import { toBig } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOAD_DESTINATIONS_QUERY_KEY = 'workload-traffic-destinations';

const getWorkloadTrafficDestinationsQueryKey = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  startTime,
  endTime,
  stepSeconds,
  limit,
}: {
  clusterId?: string;
  namespace?: string;
  workloadType?: string;
  workloadName?: string;
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
  limit?: number;
} = {}) =>
  [
    WORKLOAD_DESTINATIONS_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          namespace,
          workloadType,
          workloadName,
          startTime,
          endTime,
          stepSeconds,
          limit,
        ]
      : []),
  ] as const;

type UseWorkloadTrafficDestinationsQuery = {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  range: DateRange;
  stepSeconds?: number;
  limit?: number;
  enabled?: boolean;
};

type WorkloadTrafficDestinationsQuery = {
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  data?: WorkloadTrafficDestination<Big>[];
};

const getWorkloadTrafficDestinationsQueryOptions = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  range: [from, to],
  stepSeconds = SECONDS_IN_DAY,
  limit = 5,
  enabled = true,
}: UseWorkloadTrafficDestinationsQuery) =>
  queryOptions({
    queryKey: getWorkloadTrafficDestinationsQueryKey({
      clusterId,
      namespace,
      workloadType,
      workloadName,
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      stepSeconds,
      limit,
    }),
    queryFn: async (): Promise<WorkloadTrafficDestination<Big>[]> => {
      const { data } =
        await apiClient.costReport.getWorkloadTrafficDestinations({
          clusterId,
          namespace,
          workloadType,
          workloadName,
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          stepSeconds,
          limit,
        });

      return (
        data.destinations?.map((item) => ({
          ...item,
          ingress: {
            interZoneBytes: toBig(item.ingress?.interZoneBytes || 0),
            interZoneCost: toBig(item.ingress?.interZoneCost || 0),
          },
          egress: {
            interZoneBytes: toBig(item.egress?.interZoneBytes || 0),
            interZoneCost: toBig(item.egress?.interZoneCost || 0),
          },
        })) || []
      );
    },
    enabled,
  });

export const useWorkloadTrafficDestinationsQuery = (
  props: UseWorkloadTrafficDestinationsQuery
): WorkloadTrafficDestinationsQuery => {
  const { data, error, refetch, isLoading } = useQuery(
    getWorkloadTrafficDestinationsQueryOptions(props)
  );

  return { data, error, refetch, isLoading };
};
